import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { WMS1100_R01LotDto } from './Dto_WMS1100_R01';
import { useWMS1100_R01Store } from './Store_WMS1100_R01';

const COLUMN_STATE_KEY = 'WMS1100_R01-lot-grid-column-state';

interface Props {
	lotGridRef: React.RefObject<AgGridReact<WMS1100_R01LotDto> | null>;
	defaultWMS1100_R01LotRow: WMS1100_R01LotDto;
}

export const LotTable_WMS1100_R01 = (props: Props) => {
	const { lotGridRef, defaultWMS1100_R01LotRow } = props;

	const {
		WMS1100_R01LotList, setWMS1100_R01LotList,
		selectedWMS1100_R01LotList, setSelectedWMS1100_R01LotList, initSelectedWMS1100_R01LotList,
		lotColumnState, setLotColumnState,
		lotScrollTop, setLotScrollTop,
	} = useWMS1100_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const [clickable, setClickable] = useState(true);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'lotNo',
		gridData: WMS1100_R01LotList,
		defaultGridData: defaultWMS1100_R01LotRow,
		selectedRow: selectedWMS1100_R01LotList,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		lotCd: 'LOT No',
		lotQty: '재고량',
		printed: '출력',
	};

	const baseColumnDefs: ColDef<WMS1100_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '입고량', colId: 'lotQty', field: 'lotQty', width: 65,
			editable: (params: any) => params.data.inserted === true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 130,
			editable: (params: any) => params.data.lotInputOption === 'Y',
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		selectedIds: selectedWMS1100_R01LotList as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedWMS1100_R01LotList(ids.map(String)),
		idKey: 'lotNo' as const,
		isRowSelectable: (event: RowClickedEvent<WMS1100_R01LotDto>) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			return Boolean(event.data?.lotNo);
		},
	}), [selectedWMS1100_R01LotList, setSelectedWMS1100_R01LotList, clickable, setClickable]);

	const columnDefs: ColDef<WMS1100_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS1100_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [lotColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRowData = useMemo(() => {
		const lotQtyTotal = WMS1100_R01LotList.reduce((sum, item) => (
			sum + (selectedWMS1100_R01LotList.includes(String(item.lotNo)) ? 0 : Number(item.lotQty) || 0)
		), 0);

		return [{
			lotNo: `${WMS1100_R01LotList.length.toLocaleString()}건`,
			lotQty: lotQtyTotal,
		}];
	}, [WMS1100_R01LotList, selectedWMS1100_R01LotList]);

	return (
		<div className="flex flex-1 pb-[12px] gap-[4px] h-full min-h-full">
			<div className="w-full pb-[12px]">
				<SmallTitleSpan> 입고 Lot 목록 </SmallTitleSpan>
				<GridContextWrapper<WMS1100_R01LotDto>
					enableCellCalculation
					gridWrapperRef={gridWrapperRef}
					gridRef={lotGridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setWMS1100_R01LotList}
					initSelected={initSelectedWMS1100_R01LotList}
					defaultRowData={defaultWMS1100_R01LotRow}
					baseColumnDefs={lotColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setLotColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<WMS1100_R01LotDto>
						ref={lotGridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<WMS1100_R01LotDto>({
							idKey: 'lotNo',
							selectedList: selectedWMS1100_R01LotList,
							deletedList: [],
						})}
						shiftRangeSelection={shiftRangeSelectionConfig}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={setLotColumnState}
						scrollTop={lotScrollTop}
						setScrollTop={setLotScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
